import api from './api';
import { AxiosResponse } from 'axios';

export interface Branch {
  id: string;
  parentTenantId: string | null;
  name: string;
  slug: string;
  branchCode: string;
  businessAddress: string;
  businessLocation: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  whatsappBusinessId?: string;
  whatsappPhoneNumber?: string;
  deliveryRadiusKm?: number;
  minimumOrderAmount?: number;
  averagePreparationTime?: number;
  currentBalance: number;
  totalEarnings: number;
  totalPayouts: number;
  isActive: boolean;
  isBranch: boolean;
  isMainBranch: boolean;
  websiteEnabled: boolean;
  paymentConfig?: any;
  settings?: {
    acceptingOrders?: boolean;
    operatingHours?: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateBranchDTO {
  mainTenantId: string;
  branchName: string;
  branchAddress: string;
  branchLocation: {
    latitude: number;
    longitude: number;
  };
  branchPhone?: string;
  branchEmail?: string;
  openingHours?: {
    monday?: { open: string; close: string; isOpen: boolean };
    tuesday?: { open: string; close: string; isOpen: boolean };
    wednesday?: { open: string; close: string; isOpen: boolean };
    thursday?: { open: string; close: string; isOpen: boolean };
    friday?: { open: string; close: string; isOpen: boolean };
    saturday?: { open: string; close: string; isOpen: boolean };
    sunday?: { open: string; close: string; isOpen: boolean };
  };
  deliveryRadiusKm?: number;
  minimumOrderAmount?: number;
  averagePreparationTime?: number;
  isMainBranch?: boolean;
}

export interface UpdateBranchDTO {
  name?: string;
  businessAddress?: string;
  latitude?: number;
  longitude?: number;
  deliveryRadiusKm?: number;
  minimumOrderAmount?: number;
  averagePreparationTime?: number;
  whatsappPhoneNumber?: string;
  settings?: {
    acceptingOrders?: boolean;
    operatingHours?: any;
  };
}

export interface BranchPerformance {
  branchId: string;
  branchName: string;
  period: string;
  metrics: {
    totalOrders: number;
    revenue: number;
    averageOrderValue: number;
    rating: number;
    totalReviews: number;
  };
  growth: {
    orders: number;
    revenue: number;
  };
  topItems: Array<{
    itemId: string;
    name: string;
    orders: number;
    revenue: number;
  }>;
  comparison: {
    vsMainVendor: {
      ordersDiff: number;
      revenueDiff: number;
    };
    vsOtherBranches: {
      rank: number;
      totalBranches: number;
    };
  };
}

export interface BranchHierarchy {
  mainVendor: Branch;
  branches: Branch[];
}

export interface BranchCreationLimit {
  canCreate: boolean;
  maxBranches: number;
  currentBranches: number;
  remainingBranches?: number;
  restrictions?: {
    requiresVerification?: boolean;
    minimumRating?: number;
    minimumOrders?: number;
  };
}
class BranchService {
  private unwrapResponse<T>(response: AxiosResponse<any>): T {
    return (response.data?.data ?? response.data) as T;
  }

  /**
   * Create a new branch
   */
  async createBranch(data: CreateBranchDTO): Promise<Branch> {
    const response = await api.post<{ data: Branch }>('/api/v2/branches', data);
    return this.unwrapResponse<Branch>(response as any);
  }

  /**
   * Get all branches for a vendor
   */
  async getBranchesForVendor(tenantId: string): Promise<Branch[]> {
    const response = await api.get<{ data: Branch[] }>(`/api/v2/branches/vendors/${tenantId}/branches`);
    return this.unwrapResponse<Branch[]>(response as any);
  }

  /**
   * Get branch hierarchy (main vendor + all branches)
   */
  async getBranchHierarchy(tenantId: string): Promise<BranchHierarchy> {
    const response = await api.get<{ data: BranchHierarchy }>(`/api/v2/branches/vendors/${tenantId}/branch-hierarchy`);
    return this.unwrapResponse<BranchHierarchy>(response as any);
  }

  /**
   * Get branch details by ID
   */
  async getBranchById(branchId: string): Promise<Branch> {
    const response = await api.get<{ data: Branch }>(`/api/v2/branches/${branchId}`);
    return this.unwrapResponse<Branch>(response as any);
  }

  /**
   * Update branch
   */
  async updateBranch(branchId: string, data: UpdateBranchDTO): Promise<Branch> {
    const response = await api.put<{ data: Branch }>(`/api/v2/branches/${branchId}`, data);
    return this.unwrapResponse<Branch>(response as any);
  }

  /**
   * Deactivate branch
   */
  async deactivateBranch(branchId: string): Promise<void> {
    await api.delete(`/api/v2/branches/${branchId}`);
  }

  /**
   * Reactivate branch
   */
  async reactivateBranch(branchId: string): Promise<Branch> {
    const response = await api.post<{ data: Branch }>(`/api/v2/branches/${branchId}/reactivate`);
    return this.unwrapResponse<Branch>(response as any);
  }

  /**
   * Get branch performance metrics
   */
  async getBranchPerformance(branchId: string, period: 'week' | 'month' | 'quarter' | 'year' = 'month'): Promise<BranchPerformance> {
    const response = await api.get<{ data: BranchPerformance }>(`/api/v2/branches/${branchId}/performance`, {
      params: { period }
    });
    return this.unwrapResponse<BranchPerformance>(response as any);
  }

  /**
   * Check if vendor can create branches
   */
  async canCreateBranches(tenantId: string): Promise<BranchCreationLimit> {
    const response = await api.get<{ data: BranchCreationLimit }>('/api/v2/branches/check/can-create', {
      params: { tenantId }
    });
    return this.unwrapResponse<BranchCreationLimit>(response as any);
  }

  /**
   * Get nearby branches (public endpoint)
   */
  async getNearbyBranches(lat: number, lng: number, radius: number = 10): Promise<Branch[]> {
    const response = await api.get<{ data: Branch[] }>('/api/v1/branches/nearby', {
      params: { lat, lng, radius }
    });
    return this.unwrapResponse<Branch[]>(response as any);
  }

  /**
   * Get all branches for a tenant (public endpoint)
   */
  async getAllBranchesPublic(tenantId: string): Promise<Branch[]> {
    const response = await api.get<{ data: Branch[] }>(`/api/v1/branches/${tenantId}/all`);
    return this.unwrapResponse<Branch[]>(response as any);
  }
}

export const branchService = new BranchService();
